// conversationMemberKeys.js

const normalizeKey = (value) => String(value || '').trim();

const normalizePersonKey = (person) => (
  normalizeKey(person?.userKey || person?.key || person)
);

/**
 * Builds sorted unique member keys for a conversation, always including the current user
 * unless this is a notes-to-self conversation (only the current user selected).
 *
 * @param {object} options - Build options.
 * @param {string} options.currentUserKey - Active user key.
 * @param {Array<string|object>} options.selectedMemberKeys - Selected people from picker.
 * @returns {string[]} Sorted unique member keys.
 */
export function buildConversationMemberKeys({ currentUserKey, selectedMemberKeys = [] } = {}) {
  const current = normalizeKey(currentUserKey);
  if (!current) return [];

  const selected = (Array.isArray(selectedMemberKeys) ? selectedMemberKeys : [])
    .map((entry) => normalizePersonKey(entry))
    .filter(Boolean);

  const uniqueSelected = Array.from(new Set(selected));

  if (uniqueSelected.length === 1 && uniqueSelected[0] === current) {
    return [current];
  }

  const memberKeys = Array.from(new Set([current, ...uniqueSelected]));
  memberKeys.sort((a, b) => a.localeCompare(b));
  return memberKeys;
}

/**
 * Builds a stable hash for a conversation participant set.
 *
 * @param {string[]} memberKeys - Sorted member keys.
 * @param {string} currentUserKey - Active user key.
 * @returns {string} Participant hash.
 */
export function buildConversationMemberKeysHash(memberKeys = [], currentUserKey = '') {
  const keys = (Array.isArray(memberKeys) ? memberKeys : [])
    .map((key) => normalizeKey(key))
    .filter(Boolean);

  const current = normalizeKey(currentUserKey);
  if (keys.length === 1 && keys[0] === current) {
    return `self:${current}`;
  }

  return [...keys].sort((a, b) => a.localeCompare(b)).join('|');
}

/**
 * Sanitizes a hash segment for use in Firestore document IDs.
 *
 * @param {string} value - Raw hash value.
 * @returns {string} Sanitized segment.
 */
export function sanitizeConversationChannelKeySegment(value = '') {
  return String(value || '')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 400);
}

/**
 * Builds the deterministic Firestore document ID for a conversation.
 *
 * @param {string} memberKeysHash - Participant hash.
 * @returns {string} Channel document key.
 */
export function buildConversationChannelKey(memberKeysHash) {
  const segment = sanitizeConversationChannelKeySegment(memberKeysHash);
  return `conv_${segment}`;
}
